import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { UploadProgressService } from 'src/app/services/upload-progress.service';
import { EstampService } from 'src/app/services/estamp.service';
import { StorageService } from 'src/app/services/storage.service';
import { OrderService } from 'src/app/services/order.service';
import { UploadState } from '../doqfy-components/bulk-upload-pdf-estamp/bulk-upload.interfaces';
import {
  ApiCallStatus,
  ApproverUser,
  ReviewerBody,
  FILE_STATUS_COLOR,
  FILE_STATUS_ICON,
  FILE_STATUS_TEXT,
  IN_PROGRESS_STATUSES,
  ORDER_CONFIRMATION_PATH,
  PDF_UPLOAD_PATH,
  REDIRECT_DELAY_MS,
  SESSION_KEYS,
} from './upload-progress-popup.interfaces';

@Component({
  selector: 'app-upload-progress-popup',
  templateUrl: './upload-progress-popup.component.html',
  styleUrls: ['./upload-progress-popup.component.scss'],
  standalone: false,
})
export class UploadProgressPopupComponent implements OnInit, OnDestroy {
  uploadState: UploadState | null = null;
  private destroy$ = new Subject<void>();

  isProcessingComplete: boolean = false;
  apiCallStatus: ApiCallStatus = 'idle';
  apiErrorMessage: string = '';

  orderReviewerRequired: boolean = false;
  showReviewerForm: boolean = false;
  reviewerForm: FormGroup;
  userData: ApproverUser[] = [];
  reviewerSubmitted: boolean = false;
  isUploadFullyComplete: boolean = false;

  constructor(
    private uploadProgressService: UploadProgressService,
    private router: Router,
    private estampService: EstampService,
    private messageService: MessageService,
    private storageService: StorageService,
    private orderService: OrderService,
    private fb: FormBuilder,
  ) {
    this.reviewerForm = this.fb.group({
      reviewEmail: this.fb.array([]),
      reviewer: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.orderReviewerRequired =
      this.storageService.getOrderReviewConsent() || false;

    if (this.orderReviewerRequired) {
      this.loadApprovers();
    }

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationStart) => {
        if (!event.url.startsWith(PDF_UPLOAD_PATH)) {
          // Minimize instead of hard reset when upload is still active
          if (this.uploadState?.isActive && !this.uploadState.isMinimized) {
            this.uploadProgressService.toggleMinimize();
          }
        }
      });

    this.uploadProgressService.uploadState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (!state.isActive && state.totalFiles === 0) {
          this.resetLocalState();
          this.uploadState = state;
          return;
        }

        this.uploadState = state;

        const stillInProgress = state.files.some((f) =>
          IN_PROGRESS_STATUSES.includes(f.status),
        );

        if (
          state.isActive &&
          state.totalFiles > 0 &&
          !stillInProgress &&
          state.successCount + state.errorCount === state.totalFiles
        ) {
          this.isUploadFullyComplete = true;
        }

        const allSettled =
          state.isActive &&
          state.totalFiles > 0 &&
          !stillInProgress &&
          state.successCount + state.errorCount === state.totalFiles &&
          !this.isProcessingComplete &&
          this.apiCallStatus === 'idle';

        if (allSettled) {
          this.isProcessingComplete = true;
          this.orderReviewerRequired =
            this.storageService.getOrderReviewConsent() || false;

          if (this.orderReviewerRequired) {
            if (!this.userData.length) {
              this.loadApprovers();
            }
            this.showReviewerForm = true;
          } else {
            this.callSignalCompleteApi({});
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  private resetLocalState(): void {
    this.isProcessingComplete = false;
    this.apiCallStatus = 'idle';
    this.apiErrorMessage = '';
    this.showReviewerForm = false;
    this.reviewerSubmitted = false;
    this.isUploadFullyComplete = false;
    this.reviewerForm.reset();
    this.reviewEmailArray.clear();
  }

  /**
   * Full reset: reset local state AND tell the service to wipe its state.
   * Called when the user navigates away or explicitly closes the popup.
   */
  private hardReset(): void {
    this.resetLocalState();
    this.uploadProgressService.resetUpload();
  }

  get reviewEmailArray(): FormArray {
    return this.reviewerForm.get('reviewEmail') as FormArray;
  }

  private loadApprovers(): void {
    const account_id = this.storageService.getAccountId();
    this.orderService.getApproverEmail(account_id).subscribe((res) => {
      if (res?.content?.length) {
        this.userData = res['content'];
      }
    });
  }

  onReviewerSelect(): void {
    const selected: ApproverUser[] = this.reviewerForm.value.reviewer || [];
    this.reviewEmailArray.clear();
    selected.forEach((user) => {
      this.reviewEmailArray.push(
        this.fb.group({ email: new FormControl(user.email, Validators.email) }),
      );
    });
  }

  removeReviewer(index: number): void {
    this.reviewEmailArray.removeAt(index);
    const current: ApproverUser[] = this.reviewerForm.value.reviewer || [];
    current.splice(index, 1);
    this.reviewerForm.patchValue({ reviewer: [...current] });
    this.userData = [...this.userData];
  }

  submitReviewer(): void {
    this.reviewerSubmitted = true;
    const selected: ApproverUser[] = this.reviewerForm.value.reviewer || [];

    if (!selected || selected.length === 0) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Please select at least one Checker to proceed.',
      });
      return;
    }

    const reviewer = selected[0];
    const body: ReviewerBody = {
      reviewer_name:
        `${reviewer.first_name} ${reviewer.last_name || ''}`.trim(),
      reviewer_email: reviewer.email,
    };

    this.showReviewerForm = false;
    this.callSignalCompleteApi(body);
  }

  private async callSignalCompleteApi(body: object): Promise<void> {
    const sessionUuid = sessionStorage.getItem(SESSION_KEYS.sessionUuid);

    if (!sessionUuid) {
      this.apiCallStatus = 'error';
      this.apiErrorMessage = 'Session UUID not found';
      return;
    }

    const bulkUuid = sessionUuid;
    this.apiCallStatus = 'loading';

    try {
      await this.estampService
        .signalBulkComplete(sessionUuid, body)
        .toPromise();

      // Clean up session keys after successful API call
      Object.values(SESSION_KEYS).forEach((key) =>
        sessionStorage.removeItem(key),
      );

      this.apiCallStatus = 'success';
      this.redirectIfOnPdfUploadPage(bulkUuid);
    } catch (error: any) {
      this.apiCallStatus = 'error';
      this.apiErrorMessage =
        error?.error?.message ||
        error?.message ||
        'Failed to signal completion to server';
    }
  }

  private redirectIfOnPdfUploadPage(bulkUuid: string): void {
    if (this.router.url.startsWith(PDF_UPLOAD_PATH)) {
      setTimeout(() => {
        if (this.router.url.startsWith(PDF_UPLOAD_PATH)) {
          this.hardReset();
          this.router.navigate([ORDER_CONFIRMATION_PATH, bulkUuid], {
            queryParams: { type: 'bulk_estamp' },
          });
        }
      }, REDIRECT_DELAY_MS);
    }
  }

  retryApiCall(): void {
    this.apiCallStatus = 'idle';
    this.apiErrorMessage = '';
    this.reviewerSubmitted = false;

    if (this.orderReviewerRequired) {
      this.showReviewerForm = true;
    } else {
      this.callSignalCompleteApi({});
    }
  }

  toggleMinimize(): void {
    this.uploadProgressService.toggleMinimize();
  }

  /**
   * Close button in the expanded popup (header ✕).
   * Only allowed when no active uploads are running.
   */
  closePopup(): void {
    if (this.uploadState) {
      const hasActiveUploads = this.uploadState.files.some((f) =>
        IN_PROGRESS_STATUSES.slice(1).includes(f.status),
      );
      if (!hasActiveUploads) {
        this.hardReset();
      }
    }
  }

  /**
   * Close button shown in the minimized bar once upload is 100% done.
   * Fully resets the popup so no stale state survives into the next order.
   */
  closeMinimizedPopup(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isUploadFullyComplete) {
      this.hardReset();
    }
  }

  getOverallProgress(): number {
    if (!this.uploadState || this.uploadState.totalFiles === 0) return 0;
    const total = this.uploadState.files.reduce(
      (sum, f) => sum + f.progress,
      0,
    );
    return Math.round(total / this.uploadState.totalFiles);
  }

  getStatusText(status: string): string {
    return FILE_STATUS_TEXT[status] ?? status;
  }

  getStatusIcon(status: string): string {
    return FILE_STATUS_ICON[status] ?? 'pi pi-file';
  }

  getStatusColor(status: string): string {
    return FILE_STATUS_COLOR[status] ?? '#6c757d';
  }
}
